/**
 * Cart drawer interactions
 *
 * - Opens the drawer from the header cart icon instead of going to /cart
 * - Personal card picker (select a card + optional message, add to cart)
 * - Native Shopify discount codes (POST /cart/update.js with a `discount` key)
 * - Keeps the drawer markup and the cart counters in sync after every action
 */
(function () {
  'use strict';

  var DRAWER_SELECTOR = 'cart-drawer';
  var SECTION_ID = 'cart-drawer';

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return false;

    if (typeof drawer.open === 'function') {
      drawer.open();
    } else if (typeof window.cartOpen === 'function') {
      window.cartOpen();
    } else {
      drawer.removeAttribute('inert');
      drawer.isOpen = true;
      document.body.classList.add('open-cart');
    }
    return true;
  }

  function updateCartCounts(itemCount) {
    if (typeof itemCount !== 'number') return;

    var label = itemCount > 0 ? '(' + itemCount + ')' : '';

    document.querySelectorAll('.thb-secondary-area--item-cart-count').forEach(function (el) {
      el.textContent = label;
    });

    var drawerCount = document.querySelector('.cart-drawer-top .ct-ttl--count');
    if (drawerCount) drawerCount.textContent = label;
  }

  function reloadTrustpilot(container) {
    if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;

    container.querySelectorAll('.trustpilot-widget').forEach(function (widget) {
      window.Trustpilot.loadFromElement(widget, true);
    });
  }

  /**
   * Re-renders the drawer body from the cart-drawer section so prices, totals,
   * discount rows and the personal card state all reflect the current cart.
   */
  function refreshDrawer() {
    var drawer = getDrawer();
    if (!drawer) return Promise.resolve();

    return fetch(window.location.pathname + '?sections=' + SECTION_ID)
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var parsed = new DOMParser().parseFromString(sections[SECTION_ID], 'text/html');
        var newInner = parsed.querySelector('.cart-drawer--inner');
        var currentInner = drawer.querySelector('.cart-drawer--inner');

        if (!newInner || !currentInner) return;

        currentInner.innerHTML = newInner.innerHTML;
        drawer.classList.toggle('cart-drawer--empty', parsed.querySelector('.cart-drawer').classList.contains('cart-drawer--empty'));

        if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
        if (typeof window.productRecommended === 'function') window.productRecommended();
        reloadTrustpilot(currentInner);
      })
      .catch(function (error) {
        console.error('Error refreshing cart drawer:', error);
      });
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.disabled = isLoading;
  }

  function showError(element, message) {
    if (!element) return;

    if (message) {
      element.textContent = message;
      element.removeAttribute('hidden');
    } else {
      element.setAttribute('hidden', '');
    }
  }

  /* Header cart icon opens the drawer
     ====================================================================== */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var cartLink = event.target.closest('.thb-secondary-cart');
    if (!cartLink || cartLink.closest(DRAWER_SELECTOR)) return;

    if (openDrawer()) event.preventDefault();
  });

  /* Personal card picker
     ====================================================================== */
  document.addEventListener('click', function (event) {
    var option = event.target.closest('.cart-drawer--card-option');
    if (!option) return;

    event.preventDefault();

    var isSelected = option.getAttribute('aria-pressed') === 'true';

    option.closest('.cart-drawer--card-options').querySelectorAll('.cart-drawer--card-option').forEach(function (el) {
      el.setAttribute('aria-pressed', 'false');
    });
    option.setAttribute('aria-pressed', isSelected ? 'false' : 'true');

    showError(document.getElementById('CartDrawerCardError'), null);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.cart-drawer--card-add');
    if (!button) return;

    event.preventDefault();

    var panel = button.closest('.cart-drawer--accordion-content');
    var selected = panel.querySelector('.cart-drawer--card-option[aria-pressed="true"]');
    var errorElement = panel.querySelector('.cart-drawer--card-error');

    if (!selected) {
      showError(errorElement, errorElement.dataset.defaultMessage);
      var options = panel.querySelector('.cart-drawer--card-options');
      options.classList.add('error-shake');
      setTimeout(function () {
        options.classList.remove('error-shake');
      }, 600);
      return;
    }

    var message = panel.querySelector('.cart-drawer--card-message');
    var body = {
      id: selected.dataset.variantId,
      quantity: 1
    };

    if (message && message.value.trim()) {
      body.properties = { Note: message.value.trim() };
    }

    setLoading(button, true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status && data.status >= 400) {
          showError(errorElement, data.description || data.message);
          return;
        }

        return fetch('/cart.js')
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            updateCartCounts(cart.item_count);
            return refreshDrawer();
          });
      })
      .catch(function (error) {
        console.error('Error adding personal card:', error);
      })
      .finally(function () {
        setLoading(button, false);
      });
  });

  /* Discount codes
     ======================================================================
     Shopify replaces every code on the cart with what is sent, so codes that
     are already applied are always sent along with the new one. */
  function getAppliedCodes(panel) {
    return Array.prototype.map.call(panel.querySelectorAll('.cart-drawer--discount-remove'), function (button) {
      return button.dataset.discountCode;
    });
  }

  function saveDiscountCodes(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(function (response) {
      return response.json();
    });
  }

  function themeString(key) {
    return (window.theme && window.theme.strings && window.theme.strings[key]) || '';
  }

  function applyDiscountCode(code, panel, button) {
    var errorElement = panel.querySelector('.cart-drawer--discount-error');
    var previousCodes = getAppliedCodes(panel);
    var alreadyApplied = previousCodes.some(function (applied) {
      return applied.toLowerCase() === code.toLowerCase();
    });

    if (alreadyApplied) return Promise.resolve();

    setLoading(button, true);
    showError(errorElement, null);

    return saveDiscountCodes(previousCodes.concat([code]))
      .then(function (cart) {
        var isApplied = (cart.discount_codes || []).some(function (discount) {
          return discount.applicable && discount.code.toLowerCase() === code.toLowerCase();
        });

        if (!isApplied) {
          showError(errorElement, themeString('discountNotApplicable'));
          // Don't leave an unusable code behind on the cart.
          return saveDiscountCodes(previousCodes).then(function () {
            return false;
          });
        }

        updateCartCounts(cart.item_count);
        return refreshDrawer().then(function () {
          return true;
        });
      })
      .catch(function (error) {
        console.error('Error applying discount code:', error);
        showError(errorElement, themeString('discountError'));
      })
      .finally(function () {
        setLoading(button, false);
      });
  }

  function removeDiscountCode(code, panel, button) {
    var errorElement = panel.querySelector('.cart-drawer--discount-error');
    var remainingCodes = getAppliedCodes(panel).filter(function (applied) {
      return applied !== code;
    });

    setLoading(button, true);
    showError(errorElement, null);

    return saveDiscountCodes(remainingCodes)
      .then(function (cart) {
        updateCartCounts(cart.item_count);
        return refreshDrawer();
      })
      .catch(function (error) {
        console.error('Error removing discount code:', error);
        showError(errorElement, themeString('discountError'));
      })
      .finally(function () {
        setLoading(button, false);
      });
  }

  document.addEventListener('click', function (event) {
    var applyButton = event.target.closest('.cart-drawer--discount-apply');

    if (applyButton) {
      event.preventDefault();

      var panel = applyButton.closest('.cart-drawer--accordion-content');
      var input = panel.querySelector('.cart-drawer--discount-form input');
      var code = input.value.trim();

      if (!code) return;

      applyDiscountCode(code, panel, applyButton).then(function (applied) {
        if (applied) input.value = '';
      });
      return;
    }

    var removeButton = event.target.closest('.cart-drawer--discount-remove');

    if (removeButton) {
      event.preventDefault();
      removeDiscountCode(
        removeButton.dataset.discountCode,
        removeButton.closest('.cart-drawer--accordion-content'),
        removeButton
      );
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;

    var input = event.target.closest('.cart-drawer--discount-form input');
    if (!input) return;

    event.preventDefault();
    input.closest('.cart-drawer--discount-form').querySelector('.cart-drawer--discount-apply').click();
  });

  /* Keep counters in sync with the drawer's own quantity updates
     ====================================================================== */
  document.addEventListener('line-item:change:end', function (event) {
    if (event.detail && event.detail.cart) updateCartCounts(event.detail.cart.item_count);
  });
})();
